"use client";

import { useSearchParams } from "next/navigation";
import { useState, useMemo, useEffect } from "react";
import Link from "next/link";
import {
  Download,
  BookOpen,
  Clock,
  Globe,
  ArrowRight,
  Search,
  Calendar as CalendarIcon,
  X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PressCover from "@/components/PressCover";

interface Edition {
  _id: string;
  title: string;
  type: string;
  publishDate: string;
  featured?: boolean;
  readOnlineUrl?: string;
  rawHtml?: string;
  pdfUrl?: string;
  externalPdfUrl?: string;
  imageUrl?: string;
}

interface DigitalEditionClientProps {
  settings: any;
  initialEditions: Edition[];
}

export default function DigitalEditionClient({ settings, initialEditions }: DigitalEditionClientProps) {
  const searchParams = useSearchParams();
  const deepLinkId = searchParams.get("id");

  // 🏛️ STATE MANAGEMENT: Resets to latest (initialEditions[0]) on fresh load as requested.
  const [selectedId, setSelectedId] = useState<string>(deepLinkId || initialEditions[0]?._id || "");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [appliedDate, setAppliedDate] = useState(""); // 🏛️ To filter the archive list properly
  const [noDateFound, setNoDateFound] = useState(false);

  // 🏛️ DEEP LINK SYNC: Update selected item if URL param changes (e.g. from an email link)
  useEffect(() => {
    if (deepLinkId && initialEditions.find(e => e._id === deepLinkId)) {
      setSelectedId(deepLinkId);
      setNoDateFound(false);
    }
  }, [deepLinkId, initialEditions]);

  // 🏛️ CORE LOGIC: Find the active edition based on state
  const activeEdition = useMemo(() => {
    if (noDateFound) return null;
    return initialEditions.find(e => e._id === selectedId) || initialEditions[0];
  }, [selectedId, initialEditions, noDateFound]);

  // 🏛️ SEARCH & DATE LOGIC: Unified filtering for the archive list
  const filteredEditions = useMemo(() => {
    let list = initialEditions;

    // 🏛️ First filter by applied date
    if (appliedDate) {
      list = list.filter(e => e.publishDate.startsWith(appliedDate));
    }

    // 🏛️ Then filter by search query
    if (searchQuery) {
      list = list.filter(e =>
        e.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        e.publishDate.includes(searchQuery)
      );
    }

    return list;
  }, [searchQuery, initialEditions, appliedDate]);

  // 🏛️ CALENDAR APPLY LOGIC
  const handleApplyDate = () => {
    if (selectedDate) {
      const found = initialEditions.find(e => e.publishDate.startsWith(selectedDate));
      if (found) {
        setSelectedId(found._id);
        setAppliedDate(selectedDate);
        setNoDateFound(false);
        setSearchQuery(""); // Reset search on success
      } else {
        setAppliedDate(selectedDate); // Still apply to show empty list
        setNoDateFound(true);
      }
    }
  };

  // Reset empty state if user clicks an archive item
  const handleArchiveSelect = (id: string) => {
    setSelectedId(id);
    setNoDateFound(false);
  };

  // 🏛️ GLOBAL RESET
  const handleReset = () => {
    setSearchQuery("");
    setSelectedDate("");
    setAppliedDate("");
    setNoDateFound(false);
    setSelectedId(initialEditions[0]?._id);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric'
    });
  };

  return (
    <div className="pb-24">
      {/* 🏛️ PREMIUM MASTHEAD */}
      <header className="border-b border-border bg-background py-14 shadow-sm transition-colors duration-500">
        <div className="container mx-auto px-4 md:px-8 text-center max-w-[900px]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="brand-logo-font text-6xl md:text-8xl mb-8 tracking-tighter text-foreground">
              {settings?.pageTitle || "Digital Edition"}
            </h1>
            <p className="font-serif italic text-xl md:text-2xl text-muted-foreground/80 leading-relaxed max-w-3xl mx-auto px-6">
              {settings?.description || '"The definitive global record of movements, shifts, and civilizational intelligence, preserved in a classic format for the modern elite."'}
            </p>
          </motion.div>
        </div>
      </header>

      <main className="container mx-auto px-4 md:px-8 mt-24 max-w-[1300px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">

          {/* 🏛️ LEFT: THE DYNAMIC MAIN FRAME */}
          <div className="lg:col-span-5 relative lg:sticky lg:top-40 transition-all duration-300">
            <AnimatePresence mode="wait">
              {activeEdition && !noDateFound ? (
                <motion.div
                  key={activeEdition?._id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.4 }}
                  className="bg-muted/5 p-8 md:p-10 shadow-[0_35px_60px_-15px_rgba(0,0,0,0.1)] border border-border/40 rounded-sm group relative"
                >
                  <div className="absolute inset-0 bg-brand/[0.02] pointer-events-none" />

                  <div className="relative aspect-[3/4] bg-muted/30 mb-10 overflow-hidden shadow-inner flex items-center justify-center z-10 border border-border/30">
                    {activeEdition?.imageUrl ? (
                      <PressCover src={activeEdition.imageUrl} alt={activeEdition.title} />
                    ) : (
                      <div className="text-center p-12 opacity-20 font-serif">
                        <p className="text-4xl font-black uppercase mb-4 tracking-tighter">WPP</p>
                        <p className="text-sm uppercase tracking-[0.4em] font-bold">Historical Record</p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-6 relative z-20">
                    <div className="flex items-center justify-between border-b border-border/40 pb-4">
                      <span className="text-[10px] font-black uppercase tracking-[0.4em] text-brand">
                        {activeEdition?._id === initialEditions[0]?._id ? "Latest Intelligence" : "Archives Spotlight"}
                      </span>
                      <span className="text-[10px] font-mono font-bold text-muted-foreground uppercase opacity-70">
                        Editorial Date: {formatDate(activeEdition?.publishDate)}
                      </span>
                    </div>

                    <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight tracking-tight text-foreground">
                      {activeEdition?.title}
                    </h2>

                    <div className="flex flex-col sm:flex-row gap-4 pt-6">
                      {(activeEdition?.readOnlineUrl || activeEdition?.rawHtml) ? (
                        <Link
                          href={`/digital-edition/read/${activeEdition._id}`}
                          className="flex-1 bg-foreground text-background py-5 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-brand transition-all flex items-center justify-center gap-3 shadow-xl"
                        >
                          <BookOpen className="w-4 h-4" /> READ INTEL ONLINE
                        </Link>
                      ) : (
                        <div className="flex-1 bg-muted/20 text-muted-foreground py-5 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 border border-border cursor-default opacity-60">
                          Offline Only
                        </div>
                      )}

                      {activeEdition?.pdfUrl || activeEdition?.externalPdfUrl ? (
                        <a
                          href={activeEdition.pdfUrl ? `${activeEdition.pdfUrl}?dl=${activeEdition.title.replace(/\s+/g, '_')}.pdf` : activeEdition.externalPdfUrl}
                          download={!!activeEdition.pdfUrl}
                          target={activeEdition.pdfUrl ? "_self" : "_blank"}
                          rel={activeEdition.pdfUrl ? "" : "noopener noreferrer"}
                          className="flex-1 border-2 border-foreground py-5 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-zinc-50 transition-all flex items-center justify-center gap-3 shadow-sm"
                        >
                          <Download className="w-4 h-4" /> DOWNLOAD ARCHIVE
                        </a>
                      ) : (
                        <div className="flex-1 border-2 border-muted text-muted-foreground py-5 text-[10px] font-black uppercase tracking-[0.2em] flex items-center justify-center gap-2 opacity-30 cursor-not-allowed">
                          No Archive Available
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="empty-state"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-zinc-100/50 p-20 border-2 border-dashed border-border flex flex-col items-center justify-center text-center space-y-4"
                >
                  <CalendarIcon className="w-12 h-12 text-muted-foreground/20" />
                  <h3 className="font-serif text-2xl font-bold text-muted-foreground/60 tracking-tight">Vault Entry Not Found</h3>
                  <p className="text-muted-foreground/40 text-[10px] uppercase font-black tracking-widest leading-loose">
                    No intelligence records were published on the date: <br />
                    <span className="text-brand/60 font-mono">{appliedDate || selectedDate}</span>
                  </p>
                  <button
                    onClick={handleReset}
                    className="mt-4 text-[10px] font-black text-brand uppercase tracking-[0.3em] hover:underline"
                  >
                    View Latest Record
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* 🏛️ RIGHT: SMART ARCHIVE HUB (Search, Calendar, List) */}
          <div className="lg:col-span-7 space-y-12">

            {/* 🏛️ TOP CONTROLS: Search & Date */}
            <section className="bg-background p-6 border border-border shadow-[0_20px_50px_-15px_rgba(0,0,0,0.1)] flex flex-col md:flex-row gap-6 items-center rounded-sm">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40" />
                <input
                  type="text"
                  placeholder="SEARCH HISTORICAL BRIEFINGS..."
                  className="w-full bg-background border border-border pl-12 pr-4 py-4 text-[11px] font-bold uppercase tracking-widest focus:outline-none focus:ring-1 focus:ring-brand/40 transition-all text-foreground"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="flex w-full md:w-auto gap-2">
                <div className="relative flex-1">
                  <CalendarIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/40 pointer-events-none" />
                  <input
                    type="date"
                    className="w-full bg-background border border-border pl-12 pr-4 py-4 text-[11px] font-bold uppercase tracking-widest focus:outline-none cursor-pointer text-foreground"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                </div>
                <button
                  onClick={handleApplyDate}
                  className="px-6 bg-brand text-white text-[10px] font-black uppercase tracking-widest shadow-lg hover:bg-foreground transition-all"
                >
                  GO
                </button>
              </div>
              {(searchQuery || appliedDate || selectedDate) && (
                <button
                  onClick={handleReset}
                  className="p-4 bg-muted/20 hover:bg-brand hover:text-white transition-colors text-muted-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </section>

            {/* 🏛️ ARCHIVE VAULT LIST */}
            <section className="bg-muted/5 border border-border shadow-md overflow-hidden">
              <div className="p-6 border-b border-border bg-muted/10 flex items-center justify-between">
                <h3 className="text-[10px] font-black uppercase tracking-[0.4em] text-brand">
                  The Editorial Vault <span className="text-muted-foreground/40 ml-2">-- 730 Records</span>
                </h3>
                <span className="text-[10px] font-bold italic text-muted-foreground">Showing {filteredEditions.length} Editions</span>
              </div>

              {/* SCROLLABLE CONTAINER */}
              <div className="max-h-[800px] overflow-y-auto scrollbar-thin scrollbar-thumb-brand/20 scrollbar-track-transparent">
                {filteredEditions.length > 0 ? (
                  <div className="divide-y divide-border/40">
                    {filteredEditions.map((ed) => (
                      <button
                        key={ed._id}
                        onClick={() => handleArchiveSelect(ed._id)}
                        className={`w-full flex items-center justify-between p-6 transition-all group hover:bg-muted/10 text-left ${selectedId === ed._id && !noDateFound ? "bg-brand/[0.03] border-l-4 border-brand" : "border-l-4 border-transparent"}`}
                      >
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-3">
                            <span className="text-[9px] font-bold text-muted-foreground/40 uppercase tracking-widest">
                              {ed.type || "Intel Brief"}
                            </span>
                            {ed._id === initialEditions[0]._id && (
                              <span className="text-[8px] bg-brand/10 text-brand px-2 py-0.5 font-black uppercase">Latest</span>
                            )}
                          </div>
                          <h4 className={`font-serif text-lg font-bold group-hover:text-brand transition-colors leading-snug ${selectedId === ed._id && !noDateFound ? "text-brand" : "text-foreground/80"}`}>
                            {ed.title}
                          </h4>
                          <div className="text-[10px] font-mono text-muted-foreground flex items-center gap-2">
                            <Clock className="w-3 h-3 opacity-40" />
                            {formatDate(ed.publishDate)}
                          </div>
                        </div>
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity translate-x-4 group-hover:translate-x-0">
                          <ArrowRight className="w-4 h-4 text-brand" />
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="py-24 text-center space-y-4">
                    <div className="text-muted-foreground font-serif italic text-lg opacity-40">"No findings matching your intelligence parameters."</div>
                    <button
                      onClick={() => { setSearchQuery(""); setSelectedDate(""); setNoDateFound(false); }}
                      className="text-[10px] font-black uppercase tracking-widest text-brand hover:underline"
                    >
                      Reset Search Filters
                    </button>
                  </div>
                )}
              </div>
            </section>

            {/* 🏛️ ELITE NEWSROOM CAPABILITIES */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                { icon: Clock, title: "Same-Day Access", desc: "Digital editions are released exactly at 10:00 AM IST daily." },
                { icon: Globe, title: "Bureau Synergy", desc: "Intelligence parsed from all 12 global desks is integrated into our briefs." },
              ].map((feat, idx) => (
                <div key={idx} className="bg-muted/5 p-6 border border-border flex gap-5 shadow-sm">
                  <div className="shrink-0 p-4 bg-brand/5 text-brand h-fit rounded-full ring-1 ring-brand/10">
                    <feat.icon className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-serif font-black text-sm uppercase tracking-wide mb-2 text-foreground">{feat.title}</h4>
                    <p className="text-muted-foreground text-xs leading-relaxed font-serif italic">{feat.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CALL TO ACTION */}
            <div className="bg-foreground text-background p-10 shadow-2xl relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand/20 blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:scale-150 transition-transform duration-700" />
              <div className="relative z-10 space-y-6">
                <h3 className="font-serif text-3xl font-bold">{settings?.ctaTitle || "Secure Your Intelligence Pipeline"}</h3>
                <p className="text-background/60 text-sm max-w-xl font-serif italic leading-relaxed">
                  {settings?.ctaDescription || "WorldPrimePost's premium network delivers high-fidelity geopolitical intelligence daily. Join the global list to receive the brief directly in your secure terminal."}
                </p>
                <Link href="/newsletter" className="inline-flex items-center gap-4 bg-brand text-white px-8 py-5 text-[10px] font-black uppercase tracking-[0.3em] hover:pr-12 transition-all shadow-lg">
                  {settings?.ctaLinkText || "Join the Pulse"} <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </main>
    </div>
  );
}
